import type { Message } from '../types';
import { chatCopy } from '../../config';
import { exportSenderLabel } from '../utils/conversation-formatters';
import { messageText } from '../utils/message-text';

/**
 * #1308 (CP-14/36, C-7): the print-styled PDF path â the browser’s own print
 * dialog over a purpose-built document, NO server-side renderer. The user
 * lands in the native print sheet and chooses "Save as PDF" (or paper).
 *
 * Document rules (grammar §32.2, applied brand-neutrally):
 * - Always light-on-white: paper is the document mode regardless of app theme.
 * - Speaker-labeled turns — the user label and the per-sender display names
 *   come from the brand copy pack via the shared `exportSenderLabel`, so a
 *   voice turn carries its speaker AND its spoken mark exactly as the thread
 *   rendered them (#1731 phase 7); md, txt and print cannot drift apart.
 * - Day markers when the date changes; a time stamp on every turn.
 * - Owner-initiated only; nothing leaves the browser.
 * - #1313: it prints what is on screen, so a regenerated answer appears only
 *   in its current text. The header says so — the superseded versions are in
 *   the archive and come out of the server-side .docx/.pdf export, and a
 *   document that stayed silent about the difference would be read as the
 *   complete record.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface PrintExportOptions {
  /** Document heading; falls back to the copy pack’s markdown title. */
  title?: string;
}

/** Build the print document’s HTML. Exported for unit tests. */
export function buildPrintDocumentHtml(
  messages: Message[],
  sessionId: string,
  options: PrintExportOptions = {}
): string {
  const copy = chatCopy();
  const title = options.title?.trim() || copy.export.markdownTitle;
  const completed = messages.filter((message) => !message.isStreaming);

  let lastDayKey = '';
  const turns = completed
    .map((message) => {
      const timestamp = new Date(message.timestamp);
      const dayKey = timestamp.toDateString();
      const dayMarker =
        dayKey !== lastDayKey
          ? `<p class="day">${escapeHtml(
              Number.isNaN(timestamp.getTime())
                ? ''
                : copy.time.date(timestamp, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
            )}</p>`
          : '';
      lastDayKey = dayKey;
      const time = timestamp.toLocaleTimeString(copy.locale, { hour: '2-digit', minute: '2-digit' });
      return `${dayMarker}
<section class="turn">
  <p class="meta"><strong>${escapeHtml(exportSenderLabel(message))}</strong> · ${escapeHtml(time)}</p>
  <div class="body">${escapeHtml(messageText(message))}</div>
</section>`;
    })
    .join('\n');

  // Deliberately self-contained: inline styles only, light palette literals —
  // this is a generated print document, not app chrome, so it does not (and
  // must not) depend on the app’s theme tokens.
  // #2109 R3-B24: the document declares the reader's language, so the
  // browser's print pipeline (hyphenation, CJK line breaking, a screen
  // reader on the preview) treats it as that language.
  return `<!doctype html>
<html lang="${escapeHtml(copy.locale)}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  @page { margin: 20mm; }
  body { font-family: Georgia, 'Times New Roman', serif; color: rgb(26, 26, 26); background: white; line-height: 1.6; font-size: 12pt; max-width: 46em; margin: 0 auto; }
  header { border-bottom: 1px solid rgb(204, 204, 204); padding-bottom: 8pt; margin-bottom: 16pt; }
  header h1 { font-size: 16pt; margin: 0 0 4pt; }
  header p { margin: 0; font-size: 9pt; color: rgb(85, 85, 85); }
  header p.scope { margin-top: 3pt; font-style: italic; }
  .day { text-align: center; font-size: 9pt; letter-spacing: 0.08em; text-transform: uppercase; color: rgb(119, 119, 119); margin: 18pt 0 6pt; }
  .turn { margin: 0 0 12pt; break-inside: avoid; }
  .meta { font-size: 9pt; color: rgb(85, 85, 85); margin: 0 0 2pt; }
  .body { white-space: pre-wrap; }
</style>
</head>
<body>
<header>
  <h1>${escapeHtml(title)}</h1>
  <p>${escapeHtml(copy.export.markdownVersionLabel)} · ${escapeHtml(new Date().toLocaleString(copy.locale))} · ${escapeHtml(sessionId || copy.export.unknownSession)}</p>
  <p class="scope">${escapeHtml(copy.export.printScopeNote)}</p>
</header>
${turns}
</body>
</html>`;
}

/**
 * Open the browser print dialog over the assembled document, via a transient
 * hidden iframe. Resolves once printing has been handed to the browser.
 */
export function openPrintExport(
  messages: Message[],
  sessionId: string,
  options: PrintExportOptions = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.className = 'fixed bottom-0 right-0 h-0 w-0 border-0';
      iframe.srcdoc = buildPrintDocumentHtml(messages, sessionId, options);
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          resolve();
        } catch (printError) {
          reject(printError instanceof Error ? printError : new Error(String(printError)));
        } finally {
          // Leave the frame in the DOM long enough for the print spooler to
          // read it; Chrome/Safari both block on print(), Firefox may not.
          setTimeout(() => iframe.remove(), 60_000);
        }
      };
      document.body.appendChild(iframe);
    } catch (setupError) {
      reject(setupError instanceof Error ? setupError : new Error(String(setupError)));
    }
  });
}
